/**
 * Iter-185.L — Provider Logo CMS client cache.
 *
 * Single-flight loader for the public providers manifest. Used by
 * `CarrierLogo` so every row in `PartnerRateTable` doesn't independently
 * refetch the same payload. Refreshes whenever the `providers`
 * cache-version key bumps (admin edit / upload).
 *
 * Module-level state is intentional: we want exactly one in-flight
 * request per page load, then ~free reads from a Map.
 */
import { useEffect, useState } from "react";
import { fetchProviders } from "./api";

let _manifestPromise = null;
let _manifestSnapshot = { items_by_slug: {}, fetched_at: null };
const _subscribers = new Set();

function _notify() {
  for (const cb of _subscribers) {
    try { cb(_manifestSnapshot); } catch { /* swallow */ }
  }
}

async function _loadManifest() {
  try {
    const data = await fetchProviders();
    _manifestSnapshot = {
      items_by_slug: data?.items_by_slug || {},
      fetched_at: Date.now(),
    };
  } catch {
    // Network/CORS error — keep whatever we had. CarrierLogo falls back
    // to the filesystem-based path so the UI never breaks.
    _manifestSnapshot = {
      items_by_slug: _manifestSnapshot.items_by_slug || {},
      fetched_at: Date.now(),
    };
  } finally {
    _manifestPromise = null;
    _notify();
  }
  return _manifestSnapshot;
}

export function ensureProvidersManifest() {
  if (_manifestSnapshot.fetched_at) return Promise.resolve(_manifestSnapshot);
  if (!_manifestPromise) _manifestPromise = _loadManifest();
  return _manifestPromise;
}

export function invalidateProvidersManifest() {
  _manifestSnapshot = { items_by_slug: {}, fetched_at: null };
  _manifestPromise = null;
}

export function getProviderBySlug(slug) {
  if (!slug) return null;
  return _manifestSnapshot.items_by_slug[slug] || null;
}

/**
 * React hook — returns the cached provider record for `slug` (or null
 * while the manifest is loading / when the slug isn't catalogued).
 * Components that depend on the result should still render a sensible
 * fallback so an empty manifest doesn't blank the page.
 */
export function useProvider(slug) {
  const [provider, setProvider] = useState(() => getProviderBySlug(slug));

  useEffect(() => {
    let mounted = true;
    const cb = () => {
      if (mounted) setProvider(getProviderBySlug(slug));
    };
    _subscribers.add(cb);
    ensureProvidersManifest().then(cb);
    return () => {
      mounted = false;
      _subscribers.delete(cb);
    };
  }, [slug]);

  return provider;
}
